import React, { Dispatch, SetStateAction, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog";
import { Label } from "@components/common/label";
import { Input } from "@components/common/input";
import { Button } from "@components/common/button";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { FINANCIAL_SERVER_URL, REQUEST_AMOUNT_BOQ_ENDPOINT } from "@pma/utils/api-routes";
import { DictType } from "@pma/types";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { getFinancialErrorMessage } from "@pma/utils/helpers";
import { toast } from "@pma/hooks/use-toast";
import useTranslator from "@pma/hooks/use-translator";
import { Edit } from "lucide-react";

interface Props {
  boqItem?: DictType;
  showAdditionalAmountDialog: boolean;
  setShowAdditionalAmountDialog: Dispatch<SetStateAction<boolean>>;

  refetchBoQ: CallableFunction;
}

const RequestAdditionalAmount = ({
  showAdditionalAmountDialog,
  setShowAdditionalAmountDialog,
  boqItem,
  refetchBoQ,
}: Props) => {
  const [additionalAmount, setAdditionalAmount] = useState(boqItem?.approved_amount || 0);

  const { translate, locale } = useTranslator();
  const isHebrew = locale === "he";

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const requestAdditionalAmount = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + REQUEST_AMOUNT_BOQ_ENDPOINT,
      {
        boq_item_id: boqItem?.id,
        additional_amount: additionalAmount,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,

          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const handleRequestAmountBoQItemMutation = useMutation({
    mutationKey: ["amount-boq-item"],
    mutationFn: requestAdditionalAmount,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("boq request sent") + " — " + translate("waiting for supervisor approval"),
        variant: "success",
      });

      refetchBoQ();
      setShowAdditionalAmountDialog(false);
    },
    onError: (error: any) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "send request", translate),
        variant: "destructive",
      });
    },
  });

  const confirmAdditionalAmount = () => {
    handleRequestAmountBoQItemMutation.mutate();
  };
  return (
    <div>
      <Dialog open={showAdditionalAmountDialog} onOpenChange={setShowAdditionalAmountDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate("Request Additional Amount")}</DialogTitle>
            <DialogDescription>
              {translate("Request an additional amount for")} <strong>{boqItem?.number}</strong>.
              <br />
              <small>{boqItem?.description}</small>
            </DialogDescription>
          </DialogHeader>
          <div className='py-4'>
            <div className='grid gap-4'>
              <div className='grid gap-2'>
                <Label htmlFor='additional-amount'>{translate("Additional Amount")}</Label>
                <Input
                  id='additional-amount'
                  type='number'
                  placeholder='Enter additional amount'
                  defaultValue={boqItem?.approved_amount}
                  value={additionalAmount}
                  onChange={(e) => setAdditionalAmount(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowAdditionalAmountDialog(false)}>
              {translate("Cancel")}
            </Button>
            <Button onClick={confirmAdditionalAmount} disabled={!additionalAmount}>
              <Edit className='h-4 w-4 mr-1' />
              {translate("Submit Request")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestAdditionalAmount;
